/**
 * Release manifest contract + SemVer comparison — web client mirror (Card 10).
 *
 * CANONICAL DEFINITION: src/docs/MOBILE_VERSION_SCHEME.md
 *
 * Mirrors `api_backend_fastify_server/src/types/release.types.ts`. The two are mirrored rather
 * than shared: the API's tsconfig scopes its build to its own `src/`, so a cross-workspace import
 * would pull this app into its emit tree. Same precedent as octoally-pro's server mirroring
 * `shared-types/update-channel.ts`. The doc above is the anchor both point at.
 *
 * The schema is ADDITIVE-ONLY: tolerate unknown `artifacts` keys, and never assume a given
 * platform key is present.
 */

export const RELEASE_CHANNELS = ['beta', 'production'] as const;
export type ReleaseChannel = (typeof RELEASE_CHANNELS)[number];

/** Platform → the `artifacts` key it resolves to. iOS ships via TestFlight, so it has none. */
export const PLATFORM_ARTIFACT_KEYS = {
  android: 'android-apk',
  ios: 'ios-ipa'
} as const;

export type ReleasePlatform = keyof typeof PLATFORM_ARTIFACT_KEYS;

export interface ReleaseArtifact {
  url: string;
  latestUrl?: string;
  sha256: string;
  size?: number;
  filename?: string;
  contentType?: string;
}

export interface NativeCompat {
  /** Oldest native version still supported. A client below this floor must update. */
  minNativeVersion: string;
  android?: { minVersionCode: number };
}

export interface ReleaseManifest {
  /** SemVer MAJOR.MINOR.PATCH, no leading "v". */
  version: string;
  buildNumber?: number;
  released_at: string;
  channel: string;
  nativeCompat?: NativeCompat;
  artifacts: Record<string, ReleaseArtifact>;
  /** Present only when the request supplied a `platform`. */
  artifact?: ReleaseArtifact;
}

export const RELEASE_EVENT_TYPES = ['download', 'adoption'] as const;
export type ReleaseEventType = (typeof RELEASE_EVENT_TYPES)[number];

export interface ReleaseEventInput {
  event: ReleaseEventType;
  platform: ReleasePlatform;
  channel: ReleaseChannel;
  version: string;
  buildNumber?: number;
}

// ── SemVer ────────────────────────────────────────────────────────────────────────────────────

/**
 * Accepts `1.2.3`, a leading `v`, and trailing pre-release/build metadata (which is ignored for
 * ordering). Also tolerates the legacy 4-part `0.0.1.0` Gradle default, whose 4th segment is
 * dropped — an old native binary in the field may still report it via `App.getInfo()`.
 */
const VERSION_PATTERN = /^v?(\d+)\.(\d+)\.(\d+)(?:\.(\d+))?(?:[-+].*)?$/;

export interface ParsedVersion {
  major: number;
  minor: number;
  patch: number;
}

export function parseVersion(value: string | null | undefined): ParsedVersion | null {
  if (typeof value !== 'string') return null;
  const match = VERSION_PATTERN.exec(value.trim());
  if (!match) return null;
  return { major: Number(match[1]), minor: Number(match[2]), patch: Number(match[3]) };
}

/**
 * Numeric, per-segment comparison. Returns -1 | 0 | 1, or `null` when either side is unparseable.
 *
 * Never string-compare versions: lexically `"1.10.0" < "1.9.0"`, which would hide a real update
 * and, worse, silently mark a rollback as an upgrade.
 */
export function compareVersions(a: string, b: string): number | null {
  const left = parseVersion(a);
  const right = parseVersion(b);
  if (!left || !right) return null;
  if (left.major !== right.major) return left.major < right.major ? -1 : 1;
  if (left.minor !== right.minor) return left.minor < right.minor ? -1 : 1;
  if (left.patch !== right.patch) return left.patch < right.patch ? -1 : 1;
  return 0;
}

/**
 * True only when `candidate` is STRICTLY newer than `current`.
 *
 * Strictness is what keeps a rollback (manifest older than the running build) from prompting an
 * "update" that would downgrade the user. Unparseable input is treated as "no update".
 */
export function isNewerVersion(candidate: string, current: string): boolean {
  return compareVersions(candidate, current) === 1;
}

/**
 * True when the running version sits below the manifest's `minNativeVersion` floor, i.e. the
 * update is mandatory. Absent/unparseable `nativeCompat` means "not mandatory" — a manifest that
 * fails to state a floor must not lock users out of the app.
 */
export function isUpdateMandatory(manifest: ReleaseManifest, runningVersion: string): boolean {
  const floor = manifest.nativeCompat?.minNativeVersion;
  if (!floor) return false;
  return compareVersions(runningVersion, floor) === -1;
}

/** The artifact this platform should download, or `undefined` when the release ships none. */
export function artifactFor(
  manifest: ReleaseManifest,
  platform: ReleasePlatform
): ReleaseArtifact | undefined {
  return manifest.artifact ?? manifest.artifacts?.[PLATFORM_ARTIFACT_KEYS[platform]];
}
